import React, { useState } from "react";
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  Description,
  Check,
  ExpandLess,
  ExpandMore,
  Warning,
  TrendingUp,
  Assignment,
  PlaylistAddCheck,
  Assessment
} from "@mui/icons-material";
import {
  Button,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Collapse,
  IconButton
} from "@mui/material";
import "./BatchQualityAssessment.scss";

type FieldFailure = {
  field_name: string;
  field_value: string;
  failed_rule: string;
  failure_comment: string;
};

type FieldWiseAnalysis = {
  field_name: string;
  total_documents: number;
  passed_documents: number;
  failed_documents: number;
  pass_percentage: number;
  failure_comments?: string;
  failure_example?: string;
  failure_summary?: {
    summary: string;
    common_patterns: string[];
    root_cause: string;
    recommendations: string[];
  };
};

type DocumentWiseDetail = {
  doc_id: string;
  document_name: string;
  doc_type: string;
  pass_percentage: number;
  failures: FieldFailure[];
};

type DocumentTypeBreakdown = {
  doc_type: string;
  total_documents: number;
  documents_passed: number;
  fields_extracted: number;
  field_wise_analysis: FieldWiseAnalysis[];
};

type BatchQualityResults = {
  executive_summary: {
    summary: string;
    total_documents_checked: number;
    total_document_types: number;
    documents_passed: number;
    documents_failed: number;
    overall_quality_score: string;
    critical_issues_count: number;
    batch_id: string;
  };
  document_type_breakdown: DocumentTypeBreakdown[];
  document_wise_details: DocumentWiseDetail[];
  assessment_report: {
    executive_summary: {
      title: string;
      content: string;
    };
    overall_recommendations: {
      title: string;
      items: string[];
    };
    key_findings: {
      title: string;
      items: string[];
    };
    next_actions: {
      title: string;
      items: string[];
    };
  };
};

type BatchQualityAssessmentProps = {
  batchQualityResults: BatchQualityResults;
  onBack: () => void;
  onQualityAssessed: () => void;
};

const BatchQualityAssessment: React.FC<BatchQualityAssessmentProps> = ({
  batchQualityResults,
  onBack,
  onQualityAssessed
}) => {
  const [expandedDocTypes, setExpandedDocTypes] = useState<Set<string>>(
    new Set()
  );
  const [expandedFieldSection, setExpandedFieldSection] = useState<
    Record<string, boolean>
  >({});
  const [expandedDocSection, setExpandedDocSection] = useState<
    Record<string, boolean>
  >({});
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set());

  const toggleDocType = (docType: string) => {
    const newExpanded = new Set(expandedDocTypes);
    if (newExpanded.has(docType)) {
      newExpanded.delete(docType);
    } else {
      newExpanded.add(docType);
    }
    setExpandedDocTypes(newExpanded);
  };

  const toggleFieldSection = (docType: string) => {
    setExpandedFieldSection((prev) => ({
      ...prev,
      [docType]: !prev[docType]
    }));
  };

  const toggleDocSection = (docType: string) => {
    setExpandedDocSection((prev) => ({
      ...prev,
      [docType]: !prev[docType]
    }));
  };

  const toggleDocExpansion = (docId: string) => {
    const newExpanded = new Set(expandedDocs);
    if (newExpanded.has(docId)) {
      newExpanded.delete(docId);
    } else {
      newExpanded.add(docId);
    }
    setExpandedDocs(newExpanded);
  };

  const categoryColors: Record<string, string> = {
    Contract: "#3b82f6",
    Invoice: "#10b981",
    Receipt: "#f59e0b",
    Report: "#8b5cf6",
    Agreement: "#ef4444"
  };

  const totalDocuments =
    batchQualityResults.executive_summary.total_documents_checked;
  const totalPassed = batchQualityResults.executive_summary.documents_passed;
  const totalFailed = batchQualityResults.executive_summary.documents_failed;
  const overallPassPercentage = parseInt(
    batchQualityResults.executive_summary.overall_quality_score
  );

  return (
    <div className="batch-quality-assessment">
      {/* Header */}
      <div className="header">
        <div className="header-left">
          <div className="header-content">
            <Typography variant="h4" className="title">
              Quality Check Dashboard
            </Typography>
            <Typography variant="body1" className="subtitle">
              Overall Processing Summary - Batch{" "}
              {batchQualityResults.executive_summary.batch_id}
            </Typography>
          </div>
        </div>
        <Button
          onClick={onQualityAssessed}
          variant="contained"
          startIcon={<Check />}
          className="assess-button"
        >
          Mark as Assessed
        </Button>
      </div>

      {/* KPI Summary */}
      <Card className="kpi-summary-card">
        <CardContent className="kpi-content">
          <div className="kpi-grid">
            <div className="main-kpi">
              <div
                className={`kpi-percentage ${
                  overallPassPercentage > 75
                    ? "kpi-success-text"
                    : overallPassPercentage >= 40
                    ? "kpi-warning-text"
                    : "kpi-error-text"
                }`}
              >
                {overallPassPercentage}%
              </div>
              <div className="kpi-description">
                {totalPassed} documents passed out of {totalDocuments}
              </div>
              <LinearProgress
                value={overallPassPercentage}
                variant="determinate"
                className={`kpi-progress ${
                  overallPassPercentage > 75
                    ? "kpi-success"
                    : overallPassPercentage >= 40
                    ? "kpi-warning"
                    : "kpi-error"
                }`}
              />
            </div>
            <div className="kpi-stats">
              <Card className="stat-card total">
                <CardContent className="stat-content">
                  <div className="stat-icon">
                    <Description />
                  </div>
                  <div className="stat-info">
                    <Typography variant="body2" className="stat-label">
                      Total Documents
                    </Typography>
                    <Typography variant="h6" className="stat-value">
                      {totalDocuments}
                    </Typography>
                  </div>
                </CardContent>
              </Card>
              <Card className="stat-card passed">
                <CardContent className="stat-content">
                  <div className="stat-icon">
                    <CheckCircle />
                  </div>
                  <div className="stat-info">
                    <Typography variant="body2" className="stat-label">
                      Passed (count)
                    </Typography>
                    <Typography variant="h6" className="stat-value">
                      {totalPassed}
                    </Typography>
                  </div>
                </CardContent>
              </Card>
              <Card className="stat-card failed">
                <CardContent className="stat-content">
                  <div className="stat-icon">
                    <Cancel />
                  </div>
                  <div className="stat-info">
                    <Typography variant="body2" className="stat-label">
                      Failed (count)
                    </Typography>
                    <Typography variant="h6" className="stat-value">
                      {totalFailed}
                    </Typography>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Quality Assessment */}
      <Card className="detailed-assessment-card">
        <CardContent className="assessment-content">
          {/* <div className="main-title-section">
            <Description className="title-icon" />
            <Typography 
              variant="h4" 
              className="main-section-title"
            >
              Quality Assessment Report
            </Typography>
          </div> */}

          {/* First Row: Executive Summary and Overall Recommendations */}
          <div className="assessment-grid">
            {/* Executive Summary */}
            <div className="assessment-section">
              <div className="section-header">
                <Assessment className="section-icon warning-icon" />
                <Typography
                  variant="h6"
                  className="section-subtitle warning-text"
                >
                  {batchQualityResults.assessment_report?.executive_summary
                    ?.title || "Executive Summary"}
                </Typography>
              </div>
              <Card className="summary-card">
                <CardContent>
                  <Typography
                    variant="body1"
                    className="assessment-typography"
                    dangerouslySetInnerHTML={{
                      __html:
                        batchQualityResults.assessment_report?.executive_summary
                          ?.content || "No executive summary available."
                    }}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Overall Recommendations */}
            <div className="assessment-section">
              <div className="section-header">
                <TrendingUp className="section-icon trending-icon" />
                <Typography
                  variant="h6"
                  className="section-subtitle trending-text"
                >
                  {batchQualityResults.assessment_report
                    ?.overall_recommendations?.title ||
                    "Overall Recommendations"}
                </Typography>
              </div>
              <Card className="recommendations-card">
                <CardContent>
                  <ul className="assessment-list">
                    {(
                      batchQualityResults.assessment_report
                        ?.overall_recommendations?.items || []
                    ).map((item, index) => (
                      <li
                        key={index}
                        dangerouslySetInnerHTML={{ __html: item }}
                      />
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Second Row: Key Findings and Next Actions */}
          <div className="assessment-grid second-row">
            {/* Key Findings */}
            <div className="assessment-section">
              <div className="section-header">
                <Assignment className="section-icon assignment-icon" />
                <Typography
                  variant="h6"
                  className="section-subtitle assignment-text"
                >
                  {batchQualityResults.assessment_report?.key_findings?.title ||
                    "Key Findings"}
                </Typography>
              </div>
              <Card className="findings-card">
                <CardContent>
                  <ul className="assessment-list">
                    {(
                      batchQualityResults.assessment_report?.key_findings
                        ?.items || []
                    ).map((item, index) => (
                      <li
                        key={index}
                        dangerouslySetInnerHTML={{ __html: item }}
                      />
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Next Actions */}
            <div className="assessment-section">
              <div className="section-header">
                <PlaylistAddCheck className="section-icon checklist-icon" />
                <Typography
                  variant="h6"
                  className="section-subtitle checklist-text"
                >
                  {batchQualityResults.assessment_report?.next_actions?.title ||
                    "Next Actions"}
                </Typography>
              </div>
              <Card className="actions-card">
                <CardContent>
                  <ul className="assessment-list">
                    {(
                      batchQualityResults.assessment_report?.next_actions
                        ?.items || []
                    ).map((item, index) => (
                      <li
                        key={index}
                        dangerouslySetInnerHTML={{ __html: item }}
                      />
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document Types Section */}
      <div className="document-types-section">
        <Typography variant="h5" className="section-title">
          Document Types
        </Typography>
        <div className="doc-types-container">
          {batchQualityResults.document_type_breakdown.map((docType) => {
            const isExpanded = expandedDocTypes.has(docType.doc_type);
            const color = categoryColors[docType.doc_type] || "#64748b";
            const documentsForType =
              batchQualityResults.document_wise_details.filter(
                (doc) => doc.doc_type === docType.doc_type
              );

            return (
              <Card key={docType.doc_type} className="doc-type-card">
                <div
                  className="doc-type-header"
                  onClick={() => toggleDocType(docType.doc_type)}
                >
                  <div className="doc-type-info">
                    <div
                      className="doc-type-icon"
                      style={{ backgroundColor: `${color}20` }}
                    >
                      <Description style={{ color }} />
                    </div>
                    <div className="doc-type-details">
                      <Typography variant="h6" className="doc-type-name">
                        {docType.doc_type}
                      </Typography>
                      <Typography variant="body2" className="doc-type-stats">
                        {docType.total_documents} documents
                        {/* •{" "}
                        {docType.fields_extracted} fields extracted */}
                      </Typography>
                    </div>
                  </div>
                  <div className="doc-type-expand">
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </div>
                </div>

                <Collapse in={isExpanded}>
                  <div className="doc-type-details-content">
                    {/* Field Level Results */}
                    <Card className="results-section">
                      <div
                        className="section-header"
                        onClick={() => toggleFieldSection(docType.doc_type)}
                      >
                        <Typography variant="h6" className="section-title">
                          Field Level Results
                        </Typography>
                        {expandedFieldSection[docType.doc_type] ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )}
                      </div>

                      <Collapse in={expandedFieldSection[docType.doc_type]}>
                        <CardContent className="section-content">
                          <Typography
                            variant="body2"
                            className="section-description"
                          >
                            Aggregated accuracy per field across all documents
                            of {docType.doc_type}
                          </Typography>
                          <div className="fields-list">
                            {docType.field_wise_analysis.map((field) => {
                              const accuracy = field.pass_percentage;

                              return (
                                <div
                                  key={field.field_name}
                                  className="field-item"
                                >
                                  <div className="field-header">
                                    <Typography
                                      variant="body1"
                                      className="field-name"
                                    >
                                      {field.field_name}
                                    </Typography>
                                    <Chip
                                      label={`${accuracy.toFixed(2)}%`}
                                      className={`accuracy-chip ${
                                        accuracy > 75
                                          ? "success"
                                          : accuracy >= 40
                                          ? "warning"
                                          : "error"
                                      }`}
                                    />
                                  </div>
                                  <LinearProgress
                                    value={accuracy}
                                    variant="determinate"
                                    className={`field-progress ${
                                      accuracy > 75
                                        ? "field-success"
                                        : accuracy >= 40
                                        ? "field-warning"
                                        : "field-error"
                                    }`}
                                  />

                                  {/* Failure Summary */}
                                  {field.failure_summary &&
                                    field.failed_documents > 0 && (
                                      <div className="failure-summary">
                                        <div className="failure-header">
                                          <Warning className="warning-icon" />
                                          <Typography
                                            variant="subtitle2"
                                            className="failure-title"
                                          >
                                            Failure Summary
                                          </Typography>
                                        </div>
                                        <Typography
                                          variant="body2"
                                          className="failure-text"
                                        >
                                          {field.failure_summary.summary}
                                        </Typography>

                                        {field.failure_example && (
                                          <div className="failure-example">
                                            <strong>Example: </strong>
                                            <span>{field.failure_example}</span>
                                          </div>
                                        )}

                                        {field.failure_summary.common_patterns
                                          .length > 0 && (
                                          <div className="failure-patterns">
                                            <Typography variant="subtitle2">
                                              Common Patterns:
                                            </Typography>
                                            <ul>
                                              {field.failure_summary.common_patterns.map(
                                                (pattern, idx) => (
                                                  <li key={idx}>{pattern}</li>
                                                )
                                              )}
                                            </ul>
                                          </div>
                                        )}

                                        <div className="root-cause">
                                          <Typography variant="subtitle2">
                                            Root Cause:
                                          </Typography>
                                          <Typography variant="body2">
                                            {field.failure_summary.root_cause}
                                          </Typography>
                                        </div>

                                        {field.failure_summary.recommendations
                                          .length > 0 && (
                                          <div className="recommendations">
                                            <Typography variant="subtitle2">
                                              Recommendations:
                                            </Typography>
                                            <ul>
                                              {field.failure_summary.recommendations.map(
                                                (rec, idx) => (
                                                  <li key={idx}>{rec}</li>
                                                )
                                              )}
                                            </ul>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                  {/* {field.failed_documents === 0 && (
                                    <div className="success-message">
                                      <CheckCircle className="success-icon" />
                                      <Typography variant="body2">
                                        ✓ All data extracted correctly
                                      </Typography>
                                    </div>
                                  )} */}
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Collapse>
                    </Card>

                    {/* Document Level Results */}
                    <Card className="results-section">
                      <div
                        className="section-header"
                        onClick={() => toggleDocSection(docType.doc_type)}
                      >
                        <Typography variant="h6" className="section-title">
                          Document Level Results
                        </Typography>
                        {expandedDocSection[docType.doc_type] ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )}
                      </div>

                      <Collapse in={expandedDocSection[docType.doc_type]}>
                        <CardContent className="section-content">
                          <Typography
                            variant="body2"
                            className="section-description"
                          >
                            Per document, per field validation results for{" "}
                            {docType.doc_type}
                          </Typography>
                          <div className="documents-list">
                            {documentsForType.map((doc) => {
                              const isDocExpanded = expandedDocs.has(
                                doc.doc_id
                              );
                              const passPercentage = doc.pass_percentage;
                              const hasFailed =
                                doc.failures && doc.failures.length > 0;

                              return (
                                <div key={doc.doc_id} className="document-item">
                                  <div
                                    className="document-header"
                                    onClick={() =>
                                      toggleDocExpansion(doc.doc_id)
                                    }
                                  >
                                    <div className="document-info">
                                      <Typography
                                        variant="body1"
                                        className="document-name"
                                        title={doc.document_name}
                                      >
                                        {doc.document_name}
                                      </Typography>
                                      <Chip
                                        label={`${passPercentage.toFixed(2)}%`}
                                        className={
                                          passPercentage > 75
                                            ? "success-chip"
                                            : passPercentage >= 40
                                            ? "warning-chip"
                                            : "error-chip"
                                        }
                                      />
                                      <div className="document-progress">
                                        <LinearProgress
                                          value={passPercentage}
                                          variant="determinate"
                                          className={
                                            passPercentage > 75
                                              ? "success-progress"
                                              : passPercentage >= 40
                                              ? "warning-progress"
                                              : "error-progress"
                                          }
                                        />
                                      </div>
                                    </div>
                                    {isDocExpanded ? (
                                      <ExpandLess />
                                    ) : (
                                      <ExpandMore />
                                    )}
                                  </div>

                                  <Collapse in={isDocExpanded}>
                                    <div className="document-details">
                                      {hasFailed ? (
                                        <>
                                          <Typography
                                            variant="subtitle1"
                                            className="failures-title"
                                          >
                                            Failed Fields (
                                            {doc.failures?.length || 0})
                                          </Typography>
                                          {doc.failures?.map((failure, idx) => (
                                            <div
                                              key={idx}
                                              className="failure-item"
                                            >
                                              <div className="failure-item-header">
                                                <Typography
                                                  variant="body1"
                                                  className="failure-field"
                                                >
                                                  {failure.field_name}
                                                </Typography>
                                                <Chip
                                                  label="Failed"
                                                  className="failure-chip"
                                                />
                                              </div>
                                              <div className="failure-details">
                                                <div className="failure-detail">
                                                  <strong>Value: </strong>
                                                  <code className="failure-value">
                                                    {failure.field_value}
                                                  </code>
                                                </div>
                                                <div className="failure-detail">
                                                  <strong>Failed Rule: </strong>
                                                  <span>
                                                    {failure.failed_rule}
                                                  </span>
                                                </div>
                                                <div className="failure-comment">
                                                  <strong>
                                                    Failure Comment:
                                                  </strong>
                                                  <Typography variant="body2">
                                                    {failure.failure_comment}
                                                  </Typography>
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </>
                                      ) : (
                                        <div className="all-passed">
                                          <CheckCircle className="success-icon" />
                                          <div>
                                            <Typography
                                              variant="h6"
                                              className="success-title"
                                            >
                                              All fields passed validation
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              className="success-subtitle"
                                            >
                                              No issues detected in this
                                              document
                                            </Typography>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </Collapse>
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Collapse>
                    </Card>
                  </div>
                </Collapse>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BatchQualityAssessment;
